import React, { useState, useEffect } from "react";
import axios from "axios";
import "../../styles/SetVehicle.css"; // or combine with AddVehicle.css styles as needed
import backendUrl from "../../BackendUrl";

const SetVehicle = () => {
  // State for listing canvas
  const [activeCanvas, setActiveCanvas] = useState(null);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [error, setError] = useState(null);

  // Toggle between list view and add form view
  const [showAddForm, setShowAddForm] = useState(false);

  // State for add-form inputs (one per type)
  const [newClass, setNewClass] = useState({ class: "", description: "" });
  const [newModel, setNewModel] = useState({
    vehicleClassId: "",
    model: "",
    description: "",
  });
  const [newTrim, setNewTrim] = useState({
    vehicleModelId: "",
    trim: "",
    description: "",
  });
  const [newAccessory, setNewAccessory] = useState({
    vehicleClassId: "",
    accessory: "",
    description: "",
  });
  const [newPackage, setNewPackage] = useState({
    vehicleClassId: "",
    package: "",
    description: "",
  });

  // For select inputs in add forms (e.g. choosing a vehicle class for a model)
  const [classOptions, setClassOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);

  const adminToken = localStorage.getItem("adminToken");

  const apiEndpoints = {
    class: `${backendUrl}/api/vehicles/get-class`,
    model: `${backendUrl}/api/vehicles/get-model`,
    trim: `${backendUrl}/api/vehicles/get-trim`,
    accessory: `${backendUrl}/api/vehicles/get-accessory`,
    package: `${backendUrl}/api/vehicles/get-package`,
  };

  // Fetch options for add forms (like classes and models) when the component mounts
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const classRes = await axios.get(`${backendUrl}/api/vehicles/get-class`);
        setClassOptions(classRes.data);
      } catch (err) {
        console.error(err);
      }
      try {
        const modelRes = await axios.get(`${backendUrl}/api/vehicles/get-model`);
        setModelOptions(modelRes.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchOptions();
  }, []);

  // Handler for when one of the main buttons (class, model, etc.) is clicked
  const handleButtonClick = async (type) => {
    setActiveCanvas(type);
    setError(null);
    setShowAddForm(false); // ensure we show the listing first
    try {
      const response = await axios.get(apiEndpoints[type]);
      let fetchedData = response.data;

      // For trims, fetch the connected model to display its name
      if (type === "trim") {
        fetchedData = await Promise.all(
          fetchedData.map(async (item) => {
            if (item.vehicleModelId) {
              try {
                const modelResponse = await axios.get(
                  `${backendUrl}/api/vehicles/get-model/${item.vehicleModelId}`
                );
                return { ...item, modelName: modelResponse.data.model };
              } catch (err) {
                return { ...item, modelName: null };
              }
            }
            return item;
          })
        );
      } else if (type === "accessory" || type === "package") {
        // For accessory and package, fetch the connected class
        fetchedData = await Promise.all(
          fetchedData.map(async (item) => {
            if (item.vehicleClassId) {
              try {
                const classResponse = await axios.get(
                  `${backendUrl}/api/vehicles/get-class/${item.vehicleClassId}`
                );
                return { ...item, className: classResponse.data.class };
              } catch (err) {
                return { ...item, className: null };
              }
            }
            return item;
          })
        );
      }
      setData(fetchedData);
      setCurrentPage(1);
    } catch (error) {
      setError(`Failed to fetch ${type} data. Please try again later.`);
      console.error(`Failed to fetch ${type} data:`, error);
    }
  };

  const handleSearch = (e) => setSearch(e.target.value);

  // When a card is clicked, try to navigate to the edit page for that item
  const handleCardClick = async (id) => {
    try {
      const url = `${backendUrl}/api/vehicles/get-${activeCanvas}/${id}`;
      await axios.get(url);
      window.location.href = `/edit-${activeCanvas}/${id}`;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        alert(`This ${activeCanvas} no longer exists.`);
        setData((prevData) => prevData.filter((item) => item._id !== id));
      } else {
        alert("An error occurred. Please try again later.");
      }
    }
  };

  // Filtering and pagination logic for the list view
  const filteredData = data.filter((item) => {
    let displayName = item[activeCanvas] || "";
    if (activeCanvas === "trim") {
      displayName = `${item.modelName ? item.modelName + " - " : ""}${item[activeCanvas]}`;
    } else if (activeCanvas === "accessory" || activeCanvas === "package") {
      displayName = `${item.className ? item.className + " - " : ""}${item[activeCanvas]}`;
    }
    return displayName.toLowerCase().includes(search.toLowerCase());
  });
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  // --- Handlers for add-form input changes ---
  const handleNewClassChange = (e) =>
    setNewClass({ ...newClass, [e.target.name]: e.target.value });
  const handleNewModelChange = (e) =>
    setNewModel({ ...newModel, [e.target.name]: e.target.value });
  const handleNewTrimChange = (e) =>
    setNewTrim({ ...newTrim, [e.target.name]: e.target.value });
  const handleNewAccessoryChange = (e) =>
    setNewAccessory({ ...newAccessory, [e.target.name]: e.target.value });
  const handleNewPackageChange = (e) =>
    setNewPackage({ ...newPackage, [e.target.name]: e.target.value });

  // --- Handlers for submitting the new entries ---
  const handleAddClass = async () => {
    try {
      const response = await axios.post(
        `${backendUrl}/api/vehicles/add-class`,
        newClass,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      if (response.status === 201) {
        alert("Vehicle class added successfully");
        setNewClass({ class: "", description: "" });
        setShowAddForm(false);
        handleButtonClick("class"); // refresh listing
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Error adding vehicle class");
    }
  };

  const handleAddModel = async () => {
    try {
      const response = await axios.post(
        `${backendUrl}/api/vehicles/add-model`,
        newModel,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      if (response.status === 201) {
        alert("Vehicle model added successfully");
        setNewModel({ vehicleClassId: "", model: "", description: "" });
        setShowAddForm(false);
        handleButtonClick("model");
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Error adding vehicle model");
    }
  };

  const handleAddTrim = async () => {
    try {
      const response = await axios.post(
        `${backendUrl}/api/vehicles/add-trim`,
        newTrim,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      if (response.status === 201) {
        alert("Vehicle trim added successfully");
        setNewTrim({ vehicleModelId: "", trim: "", description: "" });
        setShowAddForm(false);
        handleButtonClick("trim");
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Error adding vehicle trim");
    }
  };

  const handleAddAccessory = async () => {
    try {
      const response = await axios.post(
        `${backendUrl}/api/vehicles/add-accessory`,
        newAccessory,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      if (response.status === 201) {
        alert("Vehicle accessory added successfully");
        setNewAccessory({ vehicleClassId: "", accessory: "", description: "" });
        setShowAddForm(false);
        handleButtonClick("accessory");
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Error adding vehicle accessory");
    }
  };

  const handleAddPackage = async () => {
    try {
      const response = await axios.post(
        `${backendUrl}/api/vehicles/add-package`,
        newPackage,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      if (response.status === 201) {
        alert("Vehicle package added successfully");
        setNewPackage({ vehicleClassId: "", package: "", description: "" });
        setShowAddForm(false);
        handleButtonClick("package");
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Error adding vehicle package");
    }
  };

  // --- Render the appropriate add form based on activeCanvas ---
  const renderAddForm = () => {
    if (!activeCanvas) return null;
    switch (activeCanvas) {
      case "class":
        return (
          <>
            <div className="canvas-overlay" onClick={() => setShowAddForm(false)}></div>
            <div className="canvas">
              <h2>Add Vehicle Class</h2>
              <input
                type="text"
                name="class"
                placeholder="Class Name"
                value={newClass.class}
                onChange={handleNewClassChange}
              />
              <textarea
                name="description"
                placeholder="Description"
                value={newClass.description}
                onChange={handleNewClassChange}
              />
              <div className="canvas-buttons">
                <button onClick={handleAddClass}>Submit</button>
                <button onClick={() => setShowAddForm(false)}>Cancel</button>
              </div>
            </div>
          </>
        );
      case "model":
        return (
          <>
            <div className="canvas-overlay" onClick={() => setShowAddForm(false)}></div>
            <div className="canvas">
              <h2>Add Vehicle Model</h2>
              <select
                name="vehicleClassId"
                value={newModel.vehicleClassId}
                onChange={handleNewModelChange}
              >
                <option value="">Select Vehicle Class</option>
                {classOptions.map((cls) => (
                  <option key={cls._id} value={cls._id}>
                    {cls.class}
                  </option>
                ))}
              </select>
              <input
                type="text"
                name="model"
                placeholder="Model Name"
                value={newModel.model}
                onChange={handleNewModelChange}
              />
              <textarea
                name="description"
                placeholder="Description"
                value={newModel.description}
                onChange={handleNewModelChange}
              />
              <div className="canvas-buttons">
                <button onClick={handleAddModel}>Submit</button>
                <button onClick={() => setShowAddForm(false)}>Cancel</button>
              </div>
            </div>
          </>
        );
      case "trim":
        return (
          <>
            <div className="canvas-overlay" onClick={() => setShowAddForm(false)}></div>
            <div className="canvas">
              <h2>Add Vehicle Trim</h2>
              <select
                name="vehicleModelId"
                value={newTrim.vehicleModelId}
                onChange={handleNewTrimChange}
              >
                <option value="">Select Vehicle Model</option>
                {modelOptions.map((model) => (
                  <option key={model._id} value={model._id}>
                    {model.model}
                  </option>
                ))}
              </select>
              <input
                type="text"
                name="trim"
                placeholder="Trim Name"
                value={newTrim.trim}
                onChange={handleNewTrimChange}
              />
              <textarea
                name="description"
                placeholder="Description"
                value={newTrim.description}
                onChange={handleNewTrimChange}
              />
              <div className="canvas-buttons">
                <button onClick={handleAddTrim}>Submit</button>
                <button onClick={() => setShowAddForm(false)}>Cancel</button>
              </div>
            </div>
          </>
        );
      case "accessory":
        return (
          <>
            <div className="canvas-overlay" onClick={() => setShowAddForm(false)}></div>
            <div className="canvas">
              <h2>Add Vehicle Accessory</h2>
              <select
                name="vehicleClassId"
                value={newAccessory.vehicleClassId}
                onChange={handleNewAccessoryChange}
              >
                <option value="">Select Vehicle Class</option>
                {classOptions.map((cls) => (
                  <option key={cls._id} value={cls._id}>
                    {cls.class}
                  </option>
                ))}
              </select>
              <input
                type="text"
                name="accessory"
                placeholder="Accessory Name"
                value={newAccessory.accessory}
                onChange={handleNewAccessoryChange}
              />
              <textarea
                name="description"
                placeholder="Description"
                value={newAccessory.description}
                onChange={handleNewAccessoryChange}
              />
              <div className="canvas-buttons">
                <button onClick={handleAddAccessory}>Submit</button>
                <button onClick={() => setShowAddForm(false)}>Cancel</button>
              </div>
            </div>
          </>
        );
      case "package":
        return (
          <>
            <div className="canvas-overlay" onClick={() => setShowAddForm(false)}></div>
            <div className="canvas">
              <h2>Add Vehicle Package</h2>
              <select
                name="vehicleClassId"
                value={newPackage.vehicleClassId}
                onChange={handleNewPackageChange}
              >
                <option value="">Select Vehicle Class</option>
                {classOptions.map((cls) => (
                  <option key={cls._id} value={cls._id}>
                    {cls.class}
                  </option>
                ))}
              </select>
              <input
                type="text"
                name="package"
                placeholder="Package Name"
                value={newPackage.package}
                onChange={handleNewPackageChange}
              />
              <textarea
                name="description"
                placeholder="Description"
                value={newPackage.description}
                onChange={handleNewPackageChange}
              />
              <div className="canvas-buttons">
                <button onClick={handleAddPackage}>Submit</button>
                <button onClick={() => setShowAddForm(false)}>Cancel</button>
              </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="edit-vehicle-page">
      {/* Main buttons for selecting the type */}
      <div className="buttons-container">
        {["class", "model", "trim", "accessory", "package"].map((type) => (
          <button
            key={type}
            className="vehicle-button"
            onClick={() => handleButtonClick(type)}
          >
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </button>
        ))}
      </div>

      {/* Render listing view or add-form view */}
      {activeCanvas && !showAddForm && (
        <div className="canvas-overlay">
          <div className="canvas">
            <button
              className="close-btn"
              onClick={() => setActiveCanvas(null)}
              aria-label="Close"
            >
              ×
            </button>
            {/* Add New button placed at the top of the canvas */}
            <button
              className="add-new-btn"
              onClick={() => setShowAddForm(true)}
            >
              Add New
            </button>
            <h2 className="canvas-title">{activeCanvas} List</h2>
            <input
              type="text"
              placeholder={`Search by ${activeCanvas}...`}
              value={search}
              onChange={handleSearch}
              className="canvas-search-input"
            />
            {error && <p className="error-message">{error}</p>}
            {currentData.length > 0 ? (
              <div className="cards-container">
                {currentData.map((item) => (
                  <div
                    key={item._id}
                    className="card"
                    onClick={() => handleCardClick(item._id)}
                  >
                    <p className="card-text">
                      {activeCanvas === "trim"
                        ? `${item.modelName ? item.modelName + " - " : ""}${item[activeCanvas]}`
                        : activeCanvas === "accessory" || activeCanvas === "package"
                        ? `${item.className ? item.className + " - " : ""}${item[activeCanvas]}`
                        : item[activeCanvas]}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-data-message">
                {filteredData.length === 0
                  ? `No ${activeCanvas} matches your search.`
                  : `No ${activeCanvas} data available.`}
              </p>
            )}
            <div className="pagination-container">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
                className={`pagination-btn ${currentPage === 1 ? "disabled" : "active"}`}
                aria-label="Previous Page"
              >
                Previous
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
                className={`pagination-btn ${currentPage === totalPages ? "disabled" : "active"}`}
                aria-label="Next Page"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
      {activeCanvas && showAddForm && renderAddForm()}
    </div>
  );
};

export default SetVehicle;